/**
 * iter v29 r242 — OnboardingTour modernizzato
 *
 * Si mostra UNA SOLA VOLTA al primo avvio dell'app nativa iOS.
 * 3 slide animate (framer-motion) che riflettono i 3 blocchi della landing:
 *   01 — Parli, è fatto       (vocale ovunque)
 *   02 — Materiali, già fatti (generati dal sopralluogo)
 *   03 — Acquisisci proprietari (trovi nuovi annunci)
 *
 * Novità r242:
 *   - Transizioni animate fra slide (fade + slide + parallax sull'icona)
 *   - Background con gradiente sottile + accent sage che cambia per slide
 *   - Chip cliccabili → aprono un bottom sheet con mini-guida della sezione
 *   - Microinterazioni (icona che "respira", hint shimmer su SCORRI)
 *   - Typography hierarchy più nitida
 *
 * Risposta a M1399: «rivedimela e fammela un po' più moderna … se uno clicca
 * su sopralluogo a voce una mini guida, nel senso che spiega cosa fa ogni
 * sezione».
 */
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Microphone,
  House,
  MagnifyingGlass,
  ArrowRight,
  CaretLeft,
  CaretRight,
  X,
  CalendarBlank,
  Sparkle,
  Image as ImageIcon,
  FileText,
  Storefront,
  WhatsappLogo,
  ChartBarHorizontal,
  Printer,
} from "@phosphor-icons/react";

const SLIDES = [
  {
    icon: Microphone,
    num: "01",
    title: "Parli, è fatto.",
    subtitle: "Vocale ovunque.",
    accent: "#7C8B6E",
    chips: [
      {
        label: "Sopralluogo a voce",
        icon: Microphone,
        body: "Durante il sopralluogo parli al telefono e scatti le foto. Pronto trascrive, corregge gli inciampi (\"bilocale anzi trilocale\") e crea l'annuncio con le foto pronte. Le foto le migliori tu con un tocco quando vuoi.",
      },
      {
        label: "Agenda vocale",
        icon: CalendarBlank,
        body: "Detta appuntamenti, follow-up e promemoria mentre sei in macchina. L'agenda li riconosce, li mette al posto giusto e ti notifica.",
      },
      {
        label: "Assistente",
        icon: Sparkle,
        body: "Una mail al cliente, una descrizione per Instagram, un riassunto della riunione. Glielo chiedi, lui scrive, tu rileggi e mandi.",
      },
    ],
  },
  {
    icon: House,
    num: "02",
    title: "Materiali, già fatti.",
    subtitle: "Generati dal sopralluogo.",
    accent: "#7C8B6E",
    chips: [
      {
        label: "Foto · restyling · arreda",
        icon: ImageIcon,
        body: "Migliora luci e colori, arreda una stanza vuota nello stile che vuoi, oppure svuota una stanza arredata per mostrare gli spazi reali.",
      },
      {
        label: "Annuncio",
        icon: FileText,
        body: "Descrizione completa, titoli accattivanti, caratteristiche tecniche già compilate. Pronto a essere pubblicato sui portali.",
      },
      {
        label: "Post & reel",
        icon: Sparkle,
        body: "Carosello Instagram, storie e reel verticali con testo e logo, pronti in un tap. La musica la aggiungi tu su Instagram o TikTok al momento del post.",
      },
      {
        label: "Vetrina personale",
        icon: Storefront,
        body: "Per ogni immobile una pagina pubblica brandizzata con il tuo logo: foto, descrizione, prima/dopo. La condividi al cliente su WhatsApp con un link.",
      },
      {
        label: "Messaggi WhatsApp",
        icon: WhatsappLogo,
        body: "Saluti, follow-up, auguri di compleanno. Pronto prepara i messaggi giusti per ogni cliente, tu li approvi e invii in un colpo.",
      },
    ],
  },
  {
    icon: MagnifyingGlass,
    num: "03",
    title: "Acquisisci proprietari.",
    subtitle: "Trovi nuovi annunci.",
    accent: "#7C8B6E",
    chips: [
      {
        label: "Stima OMI",
        icon: ChartBarHorizontal,
        body: "Valutazione immediata di qualsiasi immobile basata su dati OMI ufficiali + AVM Pronto. Mostralo al proprietario durante l'appuntamento.",
      },
      {
        label: "Volantino · stima online",
        icon: Printer,
        body: "Il tuo link \"quanto vale casa tua?\" — anonimo, 30 secondi. Stampa il volantino con QR code, mettilo nelle cassette o nelle vetrine: i proprietari ti contattano loro.",
      },
    ],
  },
];

const ONBOARDING_KEY = "pronto_onboarding_seen";

export function hasSeenOnboarding() {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === "1";
  } catch (_) { return false; }
}

export function markOnboardingSeen() {
  try { localStorage.setItem(ONBOARDING_KEY, "1"); } catch (_) { /* noop */ }
}

export default function OnboardingTour() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [selectedChip, setSelectedChip] = useState(null);
  const nav = useNavigate();
  const touchStartX = useRef(null);

  const isLast = index === SLIDES.length - 1;
  const isFirst = index === 0;
  const slide = SLIDES[index];
  const Icon = slide.icon;

  const finish = () => {
    markOnboardingSeen();
    nav("/login", { replace: true });
  };

  const next = () => {
    if (isLast) return finish();
    setDirection(1);
    setIndex((i) => Math.min(i + 1, SLIDES.length - 1));
  };

  const prev = () => {
    setDirection(-1);
    setIndex((i) => Math.max(i - 1, 0));
  };

  // Swipe gestures (mobile) — solo se nessun chip sheet aperto
  const onTouchStart = (e) => {
    if (selectedChip) return;
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (selectedChip) return;
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    if (dx < 0) next();
    else prev();
  };

  // Variants per le slide
  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-hidden"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        background:
          "radial-gradient(ellipse at top, #E8EFE0 0%, #F1F4ED 35%, #F1F4ED 100%)",
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="onboarding-tour"
    >
      {/* Decorative blob top-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full opacity-25"
        style={{
          background:
            "radial-gradient(circle, #7C8B6E 0%, rgba(124,139,110,0) 70%)",
        }}
      />
      {/* Decorative blob bottom-left */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, #C9D0BD 0%, rgba(201,208,189,0) 70%)",
        }}
      />

      {/* Top bar: Salta */}
      <div className="relative z-10 flex justify-end px-6 pt-4">
        <button
          onClick={finish}
          className="text-[14px] text-[#7C8B6E] font-medium tracking-wide hover:opacity-80 transition-opacity"
          data-testid="onboarding-skip"
        >
          Salta
        </button>
      </div>

      {/* Slide content (animated) */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 sm:px-10 text-center">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={index}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="w-full flex flex-col items-center"
          >
            {/* Hero icon — animata, "breathing" effect */}
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.05, duration: 0.5, ease: "easeOut" }}
              className="relative mb-6 sm:mb-8"
            >
              <motion.div
                animate={{
                  scale: [1, 1.04, 1],
                  boxShadow: [
                    "0 12px 32px rgba(124, 139, 110, 0.22)",
                    "0 18px 44px rgba(124, 139, 110, 0.32)",
                    "0 12px 32px rgba(124, 139, 110, 0.22)",
                  ],
                }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-[#7C8B6E] flex items-center justify-center"
              >
                <Icon size={48} weight="thin" color="#F1F4ED" />
              </motion.div>
              {/* Decorative dot ring around icon */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 -m-3 rounded-full border border-dashed border-[#C9D0BD] pointer-events-none"
              />
            </motion.div>

            {/* Numero piccolo monospace */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-[11px] tracking-[0.3em] text-[#7C8B6E] font-bold mb-3"
              data-testid="onboarding-num"
            >
              {slide.num}
            </motion.div>

            {/* Titolo editorial */}
            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-editorial text-[32px] sm:text-[38px] leading-[1.05] tracking-tight text-[#1C1C1A] mb-2 max-w-md"
              data-testid="onboarding-title"
            >
              {slide.title}
            </motion.h2>

            {/* Sottotitolo italic */}
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="italic text-[17px] text-[#7C8B6E] mb-8"
              data-testid="onboarding-subtitle"
            >
              {slide.subtitle}
            </motion.p>

            {/* Feature chips — cliccabili per mini-guida */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap items-center justify-center gap-2 max-w-md"
            >
              {slide.chips.map((chip, i) => {
                const ChipIcon = chip.icon;
                return (
                  <motion.button
                    key={i}
                    onClick={() => setSelectedChip(chip)}
                    whileTap={{ scale: 0.94 }}
                    className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-medium text-[#3A3A38] bg-white border border-[#D5DAC9] hover:border-[#7C8B6E] hover:bg-[#F8F7F2] active:bg-[#F1F4ED] transition-colors"
                    style={{
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "rgba(124,139,110,0.15)",
                    }}
                    data-testid={`onboarding-chip-${i}`}
                  >
                    {ChipIcon && (
                      <ChipIcon
                        size={13}
                        weight="duotone"
                        className="text-[#7C8B6E] group-hover:scale-110 transition-transform"
                      />
                    )}
                    <span>{chip.label}</span>
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Hint sotto le chip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.65 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="mt-4 text-[10.5px] text-[#7C8B6E] tracking-[0.05em]"
            >
              Tocca una voce per scoprire cosa fa
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Swipe hint + pagination dots */}
      <div className="relative z-10 flex flex-col items-center gap-3 mb-6">
        <div className="flex items-center gap-2 text-[#9A968B]" aria-hidden="true">
          {!isFirst && <CaretLeft size={12} weight="bold" />}
          <span className="text-[11px] tracking-[0.18em] uppercase">Scorri</span>
          {!isLast && <CaretRight size={12} weight="bold" />}
        </div>
        <div className="flex items-center justify-center gap-3">
          {SLIDES.map((_, i) => {
            const active = i === index;
            const size = active ? 12 : 8;
            return (
              <button
                key={i}
                onClick={() => {
                  setDirection(i > index ? 1 : -1);
                  setIndex(i);
                }}
                aria-label={`Vai a slide ${i + 1}`}
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  minWidth: `${size}px`,
                  minHeight: `${size}px`,
                  maxWidth: `${size}px`,
                  maxHeight: `${size}px`,
                  borderRadius: "9999px",
                  background: active ? "#5A6A4D" : "#C9D0BD",
                  boxShadow: active ? "0 2px 6px rgba(90,106,77,0.35)" : "none",
                  padding: 0,
                  border: "none",
                  flexShrink: 0,
                  appearance: "none",
                  WebkitAppearance: "none",
                  transition: "background-color 200ms, box-shadow 200ms, width 200ms",
                }}
                data-testid={`onboarding-dot-${i}`}
              />
            );
          })}
        </div>
      </div>

      {/* Bottom action */}
      <div className="relative z-10 px-6 pb-6">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={next}
          className="w-full bg-[#7C8B6E] text-white font-medium py-4 rounded-xl flex items-center justify-center gap-2 active:opacity-90 transition-opacity shadow-[0_8px_22px_-8px_rgba(124,139,110,0.55)]"
          data-testid={isLast ? "onboarding-start" : "onboarding-next"}
        >
          <span>{isLast ? "Inizia ora" : "Avanti"}</span>
          <ArrowRight size={18} weight="bold" />
        </motion.button>
      </div>

      {/* Bottom sheet mini-guida quando si tocca una chip */}
      <AnimatePresence>
        {selectedChip && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-[2px]"
              onClick={() => setSelectedChip(null)}
              data-testid="onboarding-chip-sheet-backdrop"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 280 }}
              className="fixed bottom-0 left-0 right-0 z-[61] bg-white rounded-t-3xl shadow-[0_-12px_36px_rgba(28,28,26,0.18)]"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              data-testid="onboarding-chip-sheet"
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-12 h-1.5 rounded-full bg-[#D5DAC9]" />
              </div>
              <div className="px-6 pt-2 pb-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-2xl bg-[#F1F4ED] flex items-center justify-center shrink-0">
                      {selectedChip.icon ? (
                        <selectedChip.icon
                          size={22}
                          weight="duotone"
                          color="#7C8B6E"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold">
                        Cosa fa
                      </div>
                      <div className="font-editorial text-[22px] leading-tight text-[#1C1C1A] truncate">
                        {selectedChip.label}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedChip(null)}
                    aria-label="Chiudi"
                    className="w-9 h-9 rounded-full bg-[#F1F4ED] flex items-center justify-center hover:bg-[#E8EFE0] transition-colors shrink-0"
                    data-testid="onboarding-chip-sheet-close"
                  >
                    <X size={16} weight="bold" color="#5A6A4D" />
                  </button>
                </div>
                <p
                  className="text-[14.5px] leading-relaxed text-[#3A3A38] mb-5"
                  data-testid="onboarding-chip-sheet-body"
                >
                  {selectedChip.body}
                </p>
                <button
                  onClick={() => setSelectedChip(null)}
                  className="w-full bg-[#1C1C1A] text-white font-medium py-3 rounded-xl active:opacity-90 transition-opacity"
                  data-testid="onboarding-chip-sheet-ok"
                >
                  Ho capito
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
